import { defineStore } from 'pinia'

/**
 * Playbook Store
 * 
 * This store manages playbook-related data including playbook types,
 * selected playbooks, and audience management for the application.
 * It provides reactive access to playbook configurations and audience selections.
 * 
 * Usage in components:
 * ```javascript
 * import { usePlaybookStore } from '@/stores/playbook'
 * 
 * export default {
 *   setup() {
 *     const playbookStore = usePlaybookStore()
 *     return { playbookStore }
 *   }
 * }
 * ```
 * 
 * Available getters:
 * - playbookStore.playbookTypes - Get available playbook types
 * - playbookStore.selectedPlaybook - Get currently selected playbook
 * - playbookStore.availableAudiences - Get available audiences
 * - playbookStore.selectedAudiences - Get selected audiences
 * - playbookStore.hasSelectedPlaybook - Check if a playbook is selected
 * - playbookStore.hasAudiences - Check if audiences are available
 * - playbookStore.selectedAudienceCount - Get count of selected audiences
 * 
 * Available actions:
 * - playbookStore.initialize(data) - Initialize store with data
 * - playbookStore.setPlaybookTypes(types) - Set playbook types
 * - playbookStore.setSelectedPlaybook(playbook) - Set selected playbook
 * - playbookStore.setAvailableAudiences(audiences) - Set available audiences
 * - playbookStore.setSelectedAudiences(audiences) - Set selected audiences
 * - playbookStore.addSelectedAudience(audience) - Add audience to selection
 * - playbookStore.removeSelectedAudience(audience) - Remove audience from selection
 * - playbookStore.reorderSelectedAudiences(audiences) - Reorder selected audiences
 * - playbookStore.clearPlaybook() - Clear all playbook data
 * - playbookStore.updateAssetFromAPI(asset) - Update asset via API call
 * - playbookStore.addAsset(asset) - Add asset to store
 * - playbookStore.updateAsset(assetId, updates) - Update asset in store
 * - playbookStore.removeAsset(assetId) - Remove asset from store
 */
export const usePlaybookStore = defineStore('playbook', {
    state: () => ({
        playbook_types: [], // Available playbook types
        playbooks: [], // List of active/draft playbooks for the client
        playbook_selected: {}, // Currently selected playbook
        available_audiences: [], // Audiences available for selection
        isLoaded: false, // Whether store has been initialized
        echoChannel: null, // Echo channel for real-time updates
        isSubscribed: false, // Whether currently subscribed to Echo events
    }),
    
    getters: {
        // Get playbook types
        getPlaybookTypes: (state) => state.playbook_types,

        // Get playbooks
        getPlaybooks: (state) => state.playbooks,
        
        // Get selected playbook
        getSelectedPlaybook: (state) => state.playbook_selected,
        
        // Get available audiences
        getAvailableAudiences: (state) => state.available_audiences,
                
        // Check if store is loaded
        isPlaybookLoaded: (state) => state.isLoaded,
        
        // Check if a playbook is selected
        hasSelectedPlaybook: (state) => {
            return state.playbook_selected && Object.keys(state.playbook_selected).length > 0;
        },
        
        // Get selected playbook ID
        getSelectedPlaybookId: (state) => state.playbook_selected?.id || null,
        
        // Get selected playbook name
        getSelectedPlaybookName: (state) => state.playbook_selected?.name || '',
        
        // Check if audiences are available
        hasAudiences: (state) => {
            return state.available_audiences.length > 0 || (state.playbook_selected.audiences && state.playbook_selected.audiences.length > 0);
        },
        
        // Get count of selected audiences
        selectedAudienceCount: (state) => state.playbook_selected.audiences ? state.playbook_selected.audiences.length : 0,
        
        // Get count of available audiences
        availableAudienceCount: (state) => state.available_audiences.length,
        
        // Get total audience count
        totalAudienceCount: (state) => {
            const selectedCount = state.playbook_selected.audiences ? state.playbook_selected.audiences.length : 0;
            return state.available_audiences.length + selectedCount;
        },
        
        // Check if specific audience is selected
        isAudienceSelected: (state) => (audienceId) => {
            return state.playbook_selected.audiences ? state.playbook_selected.audiences.some(a => a.id === audienceId) : false;
        },
        
        // Get audience by ID (from either selected or available)
        getAudienceById: (state) => (audienceId) => {
            const selectedAudience = state.playbook_selected.audiences ? state.playbook_selected.audiences.find(a => a.id === audienceId) : null;
            return selectedAudience || 
                   state.available_audiences.find(a => a.id === audienceId) || 
                   null;
        },
        
        // Check if playbook types are available
        hasPlaybookTypes: (state) => state.playbook_types.length > 0,
        
        // Get playbook type by ID
        getPlaybookTypeById: (state) => (typeId) => {
            return state.playbook_types.find(t => t.id === typeId) || null;
        },
        
        // Get assets sorted with primary asset first
        getSortedAssets: (state) => {
            if (!state.playbook_selected.assets) return [];
            
            return [...state.playbook_selected.assets].sort((a, b) => {
                const aIsPrimary = a.playbook_type_asset_type && a.playbook_type_asset_type.is_primary;
                const bIsPrimary = b.playbook_type_asset_type && b.playbook_type_asset_type.is_primary;
                
                if (aIsPrimary && !bIsPrimary) return -1;
                if (!aIsPrimary && bIsPrimary) return 1;
                return 0;
            });
        }
    },
    
    actions: {
        // Initialize store with data
        initialize(data) {
            if (!data) return;
            this.playbook_types = data.playbook_types || [];
            this.playbooks = data.playbooks || [];
            this.playbook_selected = data.playbook_selected || {};
            this.available_audiences = data.available_audiences || [];
            this.isLoaded = true;
        },
        
        // Set playbook types
        setPlaybookTypes(playbook_types) {
            this.playbook_types = playbook_types || [];
        },
        
        // Set selected playbook
        setSelectedPlaybook(playbook) {
            this.playbook_selected = playbook || {};
        },
        
        // Update selected playbook properties
        updateSelectedPlaybook(updates) {
            if (this.playbook_selected) {
                this.playbook_selected = { ...this.playbook_selected, ...updates };
            }
        },
        
        // Set available audiences
        setAvailableAudiences(audiences) {
            this.available_audiences = audiences || [];
        },
        
        // Add audience to selected list
        addSelectedAudience(audience) {
            if (!audience || !audience.id) return;
            
            // Ensure audiences array exists
            if (!this.playbook_selected.audiences) {
                this.playbook_selected.audiences = [];
            }
            
            // Check if already selected
            const alreadySelected = this.playbook_selected.audiences.some(a => a.id === audience.id);
            if (alreadySelected) return;
            
            // Add to selected audiences
            this.playbook_selected.audiences.push(audience);
            
            // Remove from available audiences
            const index = this.available_audiences.findIndex(a => a.id === audience.id);
            if (index > -1) {
                this.available_audiences.splice(index, 1);
            }
        },
        
        // Remove audience from selected list
        removeSelectedAudience(audience) {
            if (!audience || !audience.id) return;
            
            // Ensure audiences array exists
            if (!this.playbook_selected.audiences) {
                this.playbook_selected.audiences = [];
                return;
            }
            
            const index = this.playbook_selected.audiences.findIndex(a => a.id === audience.id);
            if (index > -1) {
                this.playbook_selected.audiences.splice(index, 1);
                
                // Add back to available audiences
                this.available_audiences.push(audience);
                
                // Sort available audiences alphabetically
                this.available_audiences.sort((a, b) => (a.name > b.name) ? 1 : -1);
            }
        },
        
        // Reorder selected audiences
        reorderSelectedAudiences(audiences) {
            if (!Array.isArray(audiences)) return;
            this.playbook_selected.audiences = audiences;
        },
        
        // Clear all selected audiences
        clearSelectedAudiences() {
            // Ensure audiences array exists
            if (!this.playbook_selected.audiences) {
                this.playbook_selected.audiences = [];
                return;
            }
            
            // Move all selected back to available
            this.available_audiences.push(...this.playbook_selected.audiences);
            this.available_audiences.sort((a, b) => (a.name > b.name) ? 1 : -1);
            this.playbook_selected.audiences = [];
        },
        
        // Clear playbook selection
        clearSelectedPlaybook() {
            this.playbook_selected = {};
        },
        
        // Clear all playbook data
        clearPlaybook() {
            this.playbook_types = [];
            this.playbook_selected = {};
            this.available_audiences = [];
            this.isLoaded = false;
        },
        
        // Reset store to initial state
        reset() {
            this.$reset();
        },
        
        // Asset Management Actions
        addAsset(asset) {
            if (!this.playbook_selected.assets) {
                this.playbook_selected.assets = [];
            }
            
            // Check if asset already exists
            const existingIndex = this.playbook_selected.assets.findIndex(a => a.id === asset.id);
            if (existingIndex >= 0) {
                // Update existing asset
                this.playbook_selected.assets[existingIndex] = { ...this.playbook_selected.assets[existingIndex], ...asset };
            } else {
                // Add new asset
                this.playbook_selected.assets.push(asset);
            }
        },
        
        updateAsset(assetId, updates) {
            if (!this.playbook_selected.assets) return;
            
            const index = this.playbook_selected.assets.findIndex(a => a.id === assetId);
            if (index >= 0) {
                this.playbook_selected.assets[index] = { ...this.playbook_selected.assets[index], ...updates };
            }
        },
        
        removeAsset(assetId) {
            if (!this.playbook_selected.assets) return;
            
            const index = this.playbook_selected.assets.findIndex(a => a.id === assetId);
            if (index >= 0) {
                this.playbook_selected.assets.splice(index, 1);
            }
        },
        
        async updateAssetFromAPI(asset) {
            try {
                const clientUrl = this.playbook_selected.client?.url || window.__CLIENT__?.url;
                const response = await window.axios.put(`/api/playbooks/${clientUrl}/assets/${asset.id}`, {
                    name: asset.name,
                    description: asset.description,
                    launch_date: asset.launch_date,
                    status: asset.status,
                    segment: asset.segment,
                    inputs: asset.inputs,
                    destination_asset_id: asset.destination_asset_id,
                    source: asset.source,
                    source_details: asset.source_details
                });

                if (response.data && response.data.asset) {
                    // Update the asset in the store with all data including versions
                    this.updateAsset(response.data.asset.id, response.data.asset);
                    return { success: true, asset: response.data.asset };
                }
                
                return { success: false, error: 'No asset data returned' };
            } catch (error) {
                console.error('Error updating asset:', error);
                return { success: false, error: error.message || 'Failed to update asset' };
            }
        },
        
        async fetchAssetFromRedis(assetKey) {
            try {
                const clientUrl = this.playbook_selected.client?.url || window.__CLIENT__?.url;
                const response = await window.axios.get(`/api/playbooks/${clientUrl}/content/${assetKey}`);
                return response.data.content;
            } catch (error) {
                console.error('Failed to fetch asset from Redis:', error);
                return null;
            }
        },
        
        // Echo Channel Management
        subscribeToAssetUpdates(clientId) {
            if (this.isSubscribed || !window.Echo || !clientId) return;
            
            // console.log('Subscribing to asset updates for client:', clientId);
            this.echoChannel = window.Echo.private('client.' + clientId)
                .listen('AssetUpdated', (e) => {
                    if (e.playbook_id === this.playbook_selected.id) {
                        this.handleAssetUpdated(e);
                    }
                });
            
            this.isSubscribed = true;
        },
        
        unsubscribeFromAssetUpdates() {
            if (this.echoChannel) {
                console.log('Unsubscribing from asset updates');
                this.echoChannel.stopListening('AssetUpdated');
                this.echoChannel = null;
                this.isSubscribed = false;
            }
        },
        
        async handleAssetUpdated(e) {
            if (e.deleted_at) {
                // Asset was deleted
                this.removeAsset(e.id);
            } else {
                // Check if asset exists
                const existingIndex = this.playbook_selected.assets ? 
                    this.playbook_selected.assets.findIndex(a => a.id === e.id) : -1;
                
                if (existingIndex >= 0) {
                    // Update existing asset - fetch full data from Redis and merge
                    const redisData = await this.fetchAssetFromRedis(e.asset_key);
                    if (redisData) {
                        // Ensure versions are included in the update
                        this.updateAsset(e.id, redisData);
                    }
                } else {
                    // New asset - add immediately with broadcast data, then fetch full details
                    const placeholderAsset = {
                        id: e.id,
                        playbook_id: e.playbook_id,
                        status: e.status,
                        name: e.name,
                        approved_at: e.approved_at,
                        // Add loading flag so UI can show loading state if needed
                        _loading: true
                    };
                    this.addAsset(placeholderAsset);
                    
                    // Fetch full asset data and update
                    const redisData = await this.fetchAssetFromRedis(e.asset_key);
                    if (redisData) {
                        // Ensure versions are included in the update
                        this.updateAsset(e.id, { ...redisData, _loading: false });
                    }
                }
            }
        }
    }
})
